import React, { useState, useEffect } from 'react';
import { fetchUserAssessmentDetails } from '../services/api';
import CertificateGenerator from './CertificateGenerator';

const ResultsPage = ({ userPK }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [certificateData, setCertificateData] = useState(null);

  useEffect(() => {
    // Fetch results data when component mounts
    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch user assessment details from the API
        const data = await fetchUserAssessmentDetails(userPK);
        
        // Check if the data is an array
        if (data && Array.isArray(data)) {
          // Log the first item to see its structure
          if (data.length > 0) {
            console.log('Sample result data:', {
              VehicleID: data[0].VehicleID,
              ScreenName: data[0].ScreenName
            });
          }
          setResults(data);
        } else if (data && Array.isArray(data.assessments)) {
          setResults(data.assessments);
        } else {
          console.error('Invalid assessment data format:', data);
          setResults([]);
          setError('Data format received from server is invalid. Please try again later.');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching results:', error);
        setResults([]);
        setError('Failed to load assessment data. Please try again later.');
        setLoading(false);
      }
    };

    fetchResults();
  }, [userPK]);

  // Open certificate modal with dynamic data
  const handleDownload = (result) => {
    // Check if the status is Incomplete, if so, don't proceed (case-insensitive)
    if (result.Status && result.Status.toLowerCase() === 'incomplete') {
      console.error('Certificate download prevented: Status is Incomplete');
      // Show an alert to the user
      window.alert('Certificate download is not available for incomplete assessments.');
      return;
    }
    
    let formattedDate = '';
    let time = '';
    
    if (result.TestTaken) {
      try {
        // Format as dd-mm-yyyy using toLocaleDateString
        formattedDate = new Date(result.TestTaken).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).replace(/\//g, '-');
      } catch (error) {
        console.error('Error formatting date:', error);
        formattedDate = result.TestTaken; // Fallback to original
      }
    }
    
    setCertificateData({
      name: result.EmployeeName,
      assessment: result.ScreenName || `Assessment ${result.AssessmentNumber}`,
      date: formattedDate,
      time: time
    });
  };

  return (
    <div className="p-6 w-full h-full bg-gray-50 ml-[-40px]">
      <h1 className="text-3xl font-semibold text-gray-800 mb-6">Result</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>{error}</p>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#404193', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                SI No.
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#584596', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Employee Name
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#623f91', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Mobile Equipment Type
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#734495', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Location
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#673a8f', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Session Number
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#823e91', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Average Score
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#954091', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Driver Score Category
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#b94995', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Status
              </th>
              <th className="px-3 py-2 text-left font-medium" style={{ backgroundColor: '#cc4995', color: 'white', borderRight: '2px solid #fff', fontSize: '13px', padding: '8px 12px' }}>
                Test Taken On
              </th>
              <th className="px-3 py-2 text-center font-medium" style={{ backgroundColor: '#dd3c99', color: 'white', fontSize: '13px', padding: '8px 12px' }}>
                Download Certificate
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {loading ? (
              <tr>
                <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                  Loading results data...
                </td>
              </tr>
            ) : results.length === 0 ? (
              <tr>
                <td colSpan="10" className="px-6 py-4 text-center text-gray-500">
                  No results found.
                </td>
              </tr>
            ) : (
              results.map((result, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.EmployeeName}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.ScreenName || `Vehicle ${result.VehicleID}`}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.LocationName || `Location ${result.LocationID}`}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.AttemptNumber}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.AverageScore}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.DriverScoreCategory}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${result.Status === 'Passed' ? 'bg-green-100 text-green-800' : 
                        result.Status === 'Failed' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'}`}>
                      {result.Status}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-900">
                    {result.TestTaken ? new Date(result.TestTaken).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }).replace(/\//g, '-') : ''}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-center">
                    {result.Status && result.Status.toLowerCase() === 'incomplete' ? (
                      <button
                        onClick={() => window.alert('Certificate download is not available for incomplete assessments.')}
                        className="px-3 py-1.5 bg-gray-400 text-white rounded-md 
                                 font-medium text-xs shadow-sm cursor-not-allowed
                                 flex items-center justify-center space-x-1 mx-auto opacity-60"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Not Available
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDownload(result)}
                        className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-md 
                                 font-medium text-xs shadow-sm hover:shadow-md transform hover:-translate-y-0.5 transition-all 
                                 duration-200 flex items-center justify-center space-x-1 focus:outline-none focus:ring-2 
                                 focus:ring-purple-500 focus:ring-opacity-50 mx-auto"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Certificate Modal */}
      {certificateData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.5)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.2)' }}>
            <CertificateGenerator
              name={certificateData.name}
              assessment={certificateData.assessment}
              date={certificateData.date}
              time={certificateData.time}
              onClose={() => setCertificateData(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultsPage; 